/**
 * Homepage hero — centered copy over floating glass platform tiles.
 *
 * Coordinates come from the approved design frames (desktop 1440×900,
 * mobile 390×844) and are treated as exact. Between those two frames the
 * desktop layer scales as a unit via the --s custom property so the tiles
 * shrink and pull toward the edges together, keeping the 80px gutter either
 * side of the center copy column at every width.
 *
 * Responsive behavior is CSS-only (no isMobile branching) because the
 * homepage is prerendered at a 390px viewport — JS-derived inline styles
 * would bake as mobile and visibly reflow when React hydrates at desktop
 * width. Both layouts therefore ship in the prerendered HTML.
 */

import React from "react";
import { useLocation } from "wouter";
import { CLIENT_TESTIMONIALS } from "@/lib/brand";
import { VIDEO_REVIEW_URL as AUDIT_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

// Real clients who gave us the testimonials on the page below — same people,
// same photos, pre-cropped to 96px squares. Decorative here: the proof line
// beside them carries the meaning, so the stack is hidden from screen readers.
const FACES = CLIENT_TESTIMONIALS.filter((t) => t.avatar).slice(0, 5);

function FaceStack({ size = 32 }: { size?: number }) {
  return (
    <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center" }}>
      {FACES.map((t, i) => (
        <img
          key={t.name}
          src={t.avatar}
          alt=""
          width={size}
          height={size}
          loading="eager"
          decoding="async"
          style={{
            width: `${size}px`, height: `${size}px`,
            borderRadius: "50%", objectFit: "cover", display: "block",
            border: "2px solid rgba(12,16,17,0.9)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.45)",
            marginLeft: i === 0 ? 0 : `-${Math.round(size * 0.3)}px`,
            position: "relative", zIndex: FACES.length - i,
          }}
        />
      ))}
    </span>
  );
}

// The audit CTA destination. Kept as one constant so it's a single-line change:
// there's no dedicated free-audit route, so this points at the existing
// conversion endpoint rather than the /seo-audit-* service landing pages.

type Tile = {
  key: string;
  name: string;        // wordmark fallback + alt text
  file: string;        // /logos/<file>
  cy?: boolean;        // cyan accent variant
  /** logo width as a % of tile size; Maps sizes by height instead */
  logo: number;
  byHeight?: boolean;
  r: string;           // base rotation
};

const TILES: Record<string, Tile> = {
  google:     { key: "google",     name: "Google",     file: "google.png",     logo: 56, r: "-9deg" },
  // Bing and Maps are taller than they are wide once trimmed (0.66 / 0.70), so
  // they size by height — capping width instead would leave them oversized.
  bing:       { key: "bing",       name: "Bing",       file: "bing.png",       logo: 58, r: "7deg", byHeight: true },
  chatgpt:    { key: "chatgpt",    name: "ChatGPT",    file: "openai.png",     logo: 55, r: "5deg", cy: true },
  reddit:     { key: "reddit",     name: "Reddit",     file: "reddit.png",     logo: 57, r: "11deg" },
  youtube:    { key: "youtube",    name: "YouTube",    file: "youtube.png",    logo: 80, r: "-6deg" },
  gemini:     { key: "gemini",     name: "Gemini",     file: "gemini.png",     logo: 78, r: "8deg" },
  copilot:    { key: "copilot",    name: "Copilot",    file: "copilot.png",    logo: 52, r: "-11deg" },
  perplexity: { key: "perplexity", name: "Perplexity", file: "perplexity.png", logo: 84, r: "-7deg", cy: true },
  claude:     { key: "claude",     name: "Claude",     file: "claude.png",     logo: 76, r: "6deg" },
  maps:       { key: "maps",       name: "Maps",       file: "maps.png",       logo: 52, r: "-4deg", byHeight: true },
};

/** Desktop placements — 1440×900 frame. `x` is left unless `fromRight`. */
type Placement = {
  t: Tile; x: number; y: number; size: number; fromRight?: boolean;
  anim: "fl1" | "fl2"; dur: string;
};

const DESKTOP: Placement[] = [
  { t: TILES.google,     x: 80,  y: 186, size: 176, anim: "fl1", dur: "11s" },
  { t: TILES.bing,       x: 272, y: 60,  size: 152, anim: "fl2", dur: "13s" },
  { t: TILES.chatgpt,    x: 150, y: 426, size: 190, anim: "fl1", dur: "10s" },
  { t: TILES.reddit,     x: 70,  y: 684, size: 162, anim: "fl2", dur: "12s" },
  { t: TILES.youtube,    x: 296, y: 700, size: 150, anim: "fl1", dur: "14s" },
  { t: TILES.gemini,     x: 92,  y: 150, size: 180, fromRight: true, anim: "fl1", dur: "12s" },
  { t: TILES.copilot,    x: 290, y: 52,  size: 150, fromRight: true, anim: "fl2", dur: "11s" },
  { t: TILES.perplexity, x: 130, y: 436, size: 184, fromRight: true, anim: "fl2", dur: "10s" },
  { t: TILES.claude,     x: 78,  y: 690, size: 158, fromRight: true, anim: "fl1", dur: "13s" },
  { t: TILES.maps,       x: 300, y: 718, size: 148, fromRight: true, anim: "fl2", dur: "15s" },
];

/** Mobile placements — 390×844 frame. Tiles deliberately bleed off both edges. */
type MobilePlacement = {
  t: Tile; x: number; size: number; fromRight?: boolean;
  y?: number; bottom?: number; opacity?: number;
  /** nudge the mark inward on tiles that bleed off-edge */
  nudge?: number;
  anim: "fl1" | "fl2"; dur: string;
};

const MOBILE: MobilePlacement[] = [
  { t: TILES.google,     x: -32, y: 52,  size: 112, nudge: 22,  anim: "fl1", dur: "12s" },
  { t: TILES.chatgpt,    x: 112, y: 126, size: 104, anim: "fl2", dur: "11s" },
  { t: TILES.gemini,     x: -30, y: 40,  size: 116, fromRight: true, nudge: -20, anim: "fl1", dur: "13s" },
  { t: TILES.bing,       x: 24,  y: 236, size: 88,  opacity: 0.85, anim: "fl2", dur: "14s" },
  { t: TILES.perplexity, x: 16,  y: 220, size: 98,  fromRight: true, anim: "fl1", dur: "10s" },
  { t: TILES.reddit,     x: -26, bottom: 120, size: 104, nudge: 21, anim: "fl2", dur: "12s" },
  { t: TILES.claude,     x: -28, bottom: 132, size: 110, fromRight: true, nudge: -21, anim: "fl1", dur: "15s" },
  { t: TILES.youtube,    x: 96,  bottom: 46,  size: 92,  opacity: 0.9,  anim: "fl2", dur: "13s" },
  { t: TILES.maps,       x: 96,  bottom: 52,  size: 84,  fromRight: true, opacity: 0.85, anim: "fl1", dur: "11s" },
];

const EYEBROW = "FULL-STACK SEO AND GEO SERVICES";

export interface HeroFloatingTilesProps {
  /** Overrides the eyebrow pill — the sales deck personalizes it per client. */
  eyebrow?: string;
  /**
   * Render the audit CTA. Off for the sales deck, which is presented
   * live on a call: there is nobody to click it, and it competes with the
   * conversation.
   */
  showCta?: boolean;
  /** Extra class on the section, for consumers that re-tune the copy column. */
  className?: string;
}

/**
 * Logo mark, falling back to the wordmark if the asset is missing.
 *
 * The mark is always attempted — there's no build-time switch to flip — so
 * dropping the files into client/public/logos/ is all it takes to light the
 * tiles up. Until then onError swaps in the wordmark, so a tile is never
 * empty or broken.
 *
 * These are above the fold: eager, never lazy, with the two accent tiles
 * prioritized. (A <link rel=preload> would be the stronger signal but has to
 * live in the document head, where it would fire on every route and 404 until
 * the assets land — fetchPriority gets the same ordering without either cost.)
 */
function TileMark({ t, nudge }: { t: Tile; nudge?: number }) {
  const [failed, setFailed] = React.useState(false);
  const nudgeStyle: React.CSSProperties = nudge
    ? nudge > 0 ? { marginLeft: `${nudge}%` } : { marginRight: `${-nudge}%` }
    : {};

  if (failed) {
    return (
      <span style={{
        fontFamily: ff, fontSize: "19px", fontWeight: 700,
        color: "rgba(255,255,255,0.72)", letterSpacing: "-0.01em",
        ...nudgeStyle,
      }}>
        {t.name}
      </span>
    );
  }
  return (
    <img
      src={`/logos/${t.file}`}
      alt={t.name}
      loading="eager"
      decoding="async"
      fetchPriority={t.cy ? "high" : "auto"}
      onError={() => setFailed(true)}
      style={{
        objectFit: "contain",
        ...(t.byHeight ? { height: `${t.logo}%`, width: "auto" } : { width: `${t.logo}%`, height: "auto" }),
        ...nudgeStyle,
      }}
    />
  );
}

/**
 * The hero's call to action: the visitor's website and the review button in
 * one step. Clarity showed the old lone button drew ~1 click per 100 homepage
 * visits while the headline beside it drew ten times that in dead clicks, so
 * the first action is now the one the form needs anyway. A plain GET form, so
 * it works before the page has hydrated; once it has, it routes in-app.
 * The review page prefills its website field from ?site=.
 */
function HeroSiteForm({ id, className }: { id: string; className: string }) {
  const [, navigate] = useLocation();
  return (
    <form
      action={AUDIT_URL}
      method="get"
      className={`hft-site ${className}`}
      onSubmit={(e) => {
        e.preventDefault();
        const site = String(new FormData(e.currentTarget).get("site") ?? "").trim();
        (window as unknown as { clarity?: (...a: unknown[]) => void }).clarity?.("event", site ? "hero_site_submit" : "hero_cta_click");
        navigate(site ? `${AUDIT_URL}?site=${encodeURIComponent(site)}` : AUDIT_URL);
      }}
    >
      <label htmlFor={id} className="hft-site-label">Your website</label>
      <input id={id} name="site" type="text" inputMode="url" autoComplete="url" placeholder="yourcompany.com" className="hft-site-input" />
      <button type="submit" className="btn-cyan hft-site-btn">Get my free video review →</button>
    </form>
  );
}

export default function HeroFloatingTiles({
  eyebrow = EYEBROW,
  showCta = true,
  className = "",
}: HeroFloatingTilesProps = {}) {
  return (
    <section className={`hft${className ? ` ${className}` : ""}`} aria-label="Indexed — full-stack SEO and GEO">
      {/* Grid overlay. Kept as its own absolutely-positioned layer rather than a
          class on the section, because `.section-grid > *` forces every direct
          child to z-index 1 and would flatten the tile/copy layering. */}
      <div className="section-grid" aria-hidden="true" style={{ position: "absolute", inset: 0, zIndex: 0 }} />

      <div className="hft-blob glow-blob" aria-hidden="true" />

      {/* ── Tiles (decorative) ── */}
      <div className="hft-tiles hft-tiles-d" aria-hidden="true">
        {DESKTOP.map((p) => (
          <div
            key={p.t.key}
            className={`hft-tile${p.t.cy ? " cy" : ""}`}
            style={{
              ["--r" as string]: p.t.r,
              animationName: p.anim,
              animationDuration: p.dur,
              width: "calc(var(--sz) * var(--s))",
              height: "calc(var(--sz) * var(--s))",
              // --oy drops the whole ring clear of the nav once scaling has
              // pulled the top row up under it.
              top: "calc(var(--ty) * var(--s) + var(--oy, 0px))",
              ["--sz" as string]: `${p.size}px`,
              ["--ty" as string]: `${p.y}px`,
              ...(p.fromRight
                ? { right: `calc(${p.x}px * var(--s))` }
                : { left: `calc(${p.x}px * var(--s))` }),
            }}
          >
            <TileMark t={p.t} />
          </div>
        ))}
      </div>

      <div className="hft-tiles hft-tiles-m" aria-hidden="true">
        {MOBILE.map((p) => (
          <div
            key={p.t.key}
            className={`hft-tile hft-tile-m${p.t.cy ? " cy" : ""}`}
            style={{
              ["--r" as string]: p.t.r,
              animationName: p.anim,
              animationDuration: p.dur,
              width: `${p.size}px`, height: `${p.size}px`,
              opacity: p.opacity,
              ...(p.fromRight ? { right: `${p.x}px` } : { left: `${p.x}px` }),
              ...(p.bottom !== undefined ? { bottom: `${p.bottom}px` } : { top: `${p.y}px` }),
            }}
          >
            <TileMark t={p.t} nudge={p.nudge} />
          </div>
        ))}
      </div>

      {/* ── Center copy (desktop / tablet) ── */}
      <div className="hft-copy">
        <span className="hft-pill">
          <span className="hft-dot" aria-hidden="true" />
          {eyebrow}
        </span>

        <h1 className="hft-h1">
          Be the answer <span style={{ color: "var(--cyan)" }}>everywhere.</span>
        </h1>

        <p className="hft-sub">
          Full-stack SEO and GEO, run as one system by specialists — so search drives revenue,
          not just rankings. Get found wherever your buyers search.
        </p>

        {showCta && (
          <HeroSiteForm id="hft-site-d" className="hft-site-d" />
        )}

        <div className="hft-proof">
          <FaceStack size={32} />
          <span>
            <span style={{ color: "var(--gold)", letterSpacing: "1px" }}>★★★★★</span>{" "}
            5.0 from 100+ client reviews · 3.5× average ROI
          </span>
        </div>
      </div>

      {/* ── Center copy card (mobile) ── */}
      <div className="hft-card">
        <span className="hft-card-eyebrow">{eyebrow}</span>
        {/* Not an <h1>: the desktop copy above is the page's one H1. Only one
            of the two is ever displayed, so this still reads as the heading
            for screen readers on mobile. */}
        <div className="hft-card-h1" role="heading" aria-level={1}>
          Be the answer <span style={{ color: "var(--cyan)" }}>everywhere.</span>
        </div>
        <p className="hft-card-sub">Search that drives revenue, not just rankings.</p>
        {showCta && <HeroSiteForm id="hft-site-m" className="hft-site-m" />}
        <div className="hft-card-proof">
          <FaceStack size={26} />
          <span>
            <span style={{ color: "var(--gold)", letterSpacing: "1px" }}>★★★★★</span>{" "}
            5.0 · 100+ reviews · 3.5× ROI
          </span>
        </div>
      </div>

      <style>{`
        .hft {
          position: relative;
          overflow: hidden;
          background: var(--ink-900);
          height: 100vh;
          min-height: 900px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .hft-blob {
          width: 820px; height: 820px;
          left: 310px; top: 60px;
          opacity: 0.3;
          z-index: 1;
        }

        /* ── Tiles ── */
        .hft-tiles { position: absolute; inset: 0; z-index: 2; }
        .hft-tiles-m { display: none; }
        .hft-tiles-d { --s: 1; }

        .hft-tile {
          position: absolute;
          border-radius: 18px;
          background: rgba(255,255,255,0.045);
          border: 1px solid var(--border-glass);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          box-shadow: 0 20px 50px rgba(0,0,0,0.5);
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          transform: rotate(var(--r, 0deg));
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        .hft-tile.cy {
          background: var(--cyan-fill);
          border-color: var(--cyan-border);
          box-shadow: 0 20px 60px rgba(87,215,226,0.16);
        }
        .hft-tile-m { border-radius: 15px; }

        @keyframes fl1 {
          0%, 100% { transform: translateY(0) rotate(var(--r, 0deg)); }
          50%      { transform: translateY(-22px) rotate(calc(var(--r, 0deg) + 3deg)); }
        }
        @keyframes fl2 {
          0%, 100% { transform: translateY(0) rotate(var(--r, 0deg)); }
          50%      { transform: translateY(18px) rotate(calc(var(--r, 0deg) - 4deg)); }
        }

        /* ── Center copy ── */
        .hft-copy {
          position: relative;
          z-index: 5;
          display: flex; flex-direction: column; align-items: center;
          text-align: center;
          padding: 80px 72px 0;
          width: 100%;
        }
        .hft-pill {
          display: inline-flex; align-items: center; gap: 9px;
          padding: 8px 18px; border-radius: 88px;
          background: rgba(255,255,255,0.04);
          border: 1px solid var(--border-glass);
          font-family: ${ff}; font-size: 12.5px; font-weight: 700;
          letter-spacing: 0.14em; text-transform: uppercase;
          color: rgba(255,255,255,0.68);
          margin-bottom: 30px;
        }
        .hft-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: var(--green); box-shadow: 0 0 8px var(--green);
          flex-shrink: 0;
        }
        .hft-h1 {
          font-family: ${ff};
          font-size: clamp(64px, 7.5vw, 108px);
          font-weight: 800;
          line-height: 0.92;
          letter-spacing: -0.045em;
          max-width: 15ch;
          text-wrap: balance;
          color: #fff;
          margin: 0 0 26px;
        }
        .hft-sub {
          font-family: ${ff};
          max-width: 52ch;
          font-size: 20px; line-height: 1.6;
          color: rgba(255,255,255,0.72);
          text-wrap: pretty;
          margin: 0 0 40px;
        }
        .hft-site {
          display: flex; align-items: center; gap: 8px;
          max-width: 540px; margin: 0 auto; padding: 6px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.16);
          border-radius: 88px;
        }
        .hft-site:focus-within { border-color: var(--cyan); }
        .hft-site-label {
          position: absolute; width: 1px; height: 1px; overflow: hidden;
          clip: rect(0 0 0 0); white-space: nowrap;
        }
        .hft-site-input {
          flex: 1; min-width: 0; height: 46px; padding: 0 18px;
          background: transparent; border: 0; outline: none;
          font-family: ${ff}; font-size: 16px; color: #fff;
        }
        .hft-site-input::placeholder { color: rgba(255,255,255,0.45); }
        .hft-site-btn { height: 46px; padding: 0 24px; font-size: 15px; cursor: pointer; }
        .hft-proof {
          font-family: ${ff};
          margin: 30px 0 0;
          font-size: 15px; font-weight: 600;
          color: rgba(255,255,255,0.7);
          display: flex; align-items: center; justify-content: center;
          gap: 12px; flex-wrap: wrap;
        }

        /* Mobile card is desktop-hidden */
        .hft-card { display: none; }

        /* ── Scale the desktop tile layer as a unit between the two frames.
              Offsets and sizes share --s, so the columns pull toward the edges
              as they shrink and the center gutter is preserved. ── */
        @media (max-width: 1439px) { .hft-tiles-d { --s: 0.84; --oy: 26px; } }

        /* Below 1200 the inner tiles (Bing / Copilot / YouTube / Maps) sit at
           x-offsets of 272–300, so scaling walks them into the copy column
           before it moves them out of the way. The ring drops to its six outer
           tiles here rather than break the 80px gutter. */
        @media (max-width: 1199px) {
          .hft-tiles-d { --s: 0.74; --oy: 40px; }
          .hft-tiles-d .hft-tile:nth-child(2),
          .hft-tiles-d .hft-tile:nth-child(5),
          .hft-tiles-d .hft-tile:nth-child(7),
          .hft-tiles-d .hft-tile:nth-child(10) { display: none; }
          .hft-copy { padding: 80px 40px 0; }
          .hft-blob { width: 620px; height: 620px; left: 120px; }
        }
        @media (max-width: 1023px) { .hft-tiles-d { --s: 0.62; --oy: 52px; } }

        /* ── Mobile frame (390×844) ──
           Switches at 900px, matching where the nav drops to its mobile bar.
           The design only specifies 1440 and 390 frames; scoping this to 480px
           left 481–900 rendering the desktop copy column at phone widths, where
           the tiles cross straight through the headline. Large phones report
           CSS widths above 480 (and any phone does in landscape), so that band
           is real traffic, not just tablets. */
        @media (max-width: 900px) {
          .hft {
            height: 100vh; min-height: 720px;
            align-items: stretch;
          }
          .hft-tiles-d { display: none; }
          .hft-tiles-m { display: block; }
          .hft-copy { display: none; }
          .hft-blob { width: 600px; height: 600px; left: -105px; top: -120px; opacity: 0.3; }

          .hft-card {
            display: flex; flex-direction: column;
            position: absolute; top: 50%; transform: translateY(-50%);
            /* 22px page padding at phone widths; capped and centered above that
               so the card doesn't stretch across a small tablet. */
            left: 22px; right: 22px; max-width: 430px;
            margin-left: auto; margin-right: auto;
            z-index: 5;
            background: rgba(10,12,12,0.78);
            border: 1px solid var(--border-glass-2);
            border-radius: 22px;
            backdrop-filter: blur(22px);
            -webkit-backdrop-filter: blur(22px);
            box-shadow: 0 26px 80px rgba(0,0,0,0.62);
            padding: 28px 22px 26px;
            text-align: center;
          }
          .hft-card-eyebrow {
            font-family: ${ff}; font-size: 10.5px; font-weight: 700;
            letter-spacing: 0.14em; text-transform: uppercase;
            color: var(--cyan); margin-bottom: 14px;
          }
          .hft-card-h1 {
            font-family: ${ff}; font-size: 50px; font-weight: 800;
            line-height: 0.94; letter-spacing: -0.04em;
            color: #fff; margin: 0 0 14px;
          }
          .hft-card-sub {
            font-family: ${ff}; font-size: 15.5px; line-height: 1.55;
            color: rgba(255,255,255,0.72); margin: 0 0 20px;
          }
          .hft-site-m {
            flex-direction: column; align-items: stretch; gap: 10px;
            width: 100%; max-width: none; padding: 0;
            background: none; border: 0; border-radius: 0;
          }
          .hft-site-m .hft-site-input {
            flex: none; height: 50px; border-radius: 88px; text-align: center;
            background: rgba(255,255,255,0.06);
            border: 1px solid rgba(255,255,255,0.16);
          }
          .hft-site-m .hft-site-input:focus { border-color: var(--cyan); }
          .hft-site-m .hft-site-btn { height: 52px; width: 100%; }
          .hft-card-proof {
            font-family: ${ff}; font-size: 12.5px; font-weight: 600;
            color: rgba(255,255,255,0.7); margin: 16px 0 0;
            display: flex; align-items: center; justify-content: center;
            gap: 9px; flex-wrap: wrap;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hft-tile { animation: none !important; transform: rotate(var(--r, 0deg)); }
        }
      `}</style>
    </section>
  );
}
